/**
 * LayoutEditor.jsx — the manual layout editor workspace.
 *
 * The nest gets a sheet very close to right and, often enough, not right. This
 * screen is where an operator finishes it by hand: move, rotate, add, remove
 * cartons, and resize the sheet under them. It loads from a finished
 * /export/vector run ("Open in editor" on Auto Imposer) and exports through
 * exactly the same POST /export/vector/pdf as an untouched nest.
 *
 * All model logic lives in editorDoc.js — this file is interaction and paint.
 * That split is deliberate: the arithmetic that decides where a die actually
 * lands on the plate is pinned by 119 assertions in node, and none of it has to
 * be reached through a rendered component to be tested.
 *
 * ─── Geometry, same rules as VectorLayoutView ──────────────────────────────
 *   · 1 SVG user unit = 1 mm, pan/zoom via a <g transform> (useCanvasControls).
 *   · Y-flip on every vertex: MNE Y-up → SVG Y-down via  y_svg = shH - y_mne.
 *     Which also inverts drag: dragging DOWN the screen decreases sheet Y.
 *   · An L-shaped die draws as a <polygon> with all N points, so its notch
 *     stays empty and stays clickable-through.
 *   · A tile draws its `components` (the two real cartons), not just the fused
 *     outline — the same rule the read-only renderer follows.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCanvasControls } from './useCanvasControls';
import { useTheme } from './theme';
import LenInput from './LenInput';
import MarginFields from './MarginFields';
import SheetChrome, { DotGrid } from './SheetChrome';
import { RemoveBordersSwitch, BorderReportList, PressSheetSwitches, PressReportList } from './PressPdfBorders';
import {
  unplaceableCartons, cartonTypeFor, reportEntryFor, tileConfigFrom,
} from './artworkPlacement';
import {
  docToVectorResult, pieceGeometry, protoOf, computeConflicts,
  usableArea, docStats, hitTest, snapDelta, findFreeSpot,
  piecesInRect, piecesOfSource, piecesOfCartonType, selectableGroups, unionSelection,
  movePieces, rotatePieces, removePieces, addPiece, duplicatePieces,
  setSheet, setPiecePosition, normalizeAngle, rotatedSize,
  editorApply, editorUndo, editorRedo,
  addTile, ensureTilePrototype, isTileable,
  addPrepressTile, hasPrepressTile, prepressTileKey,
  marginsAreUniform, collapseToUniform, marginCollapseError, ZERO_MARGINS,
  offersCustomPair,
  exportBlockReason,
  removeSourceCarton,
} from './editorDoc';

/** Nudge distances, mm. Shift coarsens, Alt refines. */
const NUDGE = { fine: 0.1, normal: 1, coarse: 10 };
/** Snap pull, in SCREEN pixels — converted to mm against live zoom so the
 *  magnet stays the same size on screen at every zoom level. */
const SNAP_PX = 7;
/** Offset for a duplicated piece, mm — far enough to see, near enough to place. */
const DUP_OFFSET = 10;

/* The app's TopBar is position:sticky, top:0, height 56 (App.js:274-276). The
 * rail pins 16 below it and keeps 16 clear at the bottom. One place for both
 * numbers, so the pin and the height cap cannot drift apart — a literal 72 in
 * one style object and an 88 in another is how a rail ends up pinned under a
 * header it overlaps by 4. */
const RAIL_TOP = 72;
const RAIL_BOTTOM = 16;

const isTypingTarget = (el) => {
  const tag = el?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable;
};

/* ══════════════════════════════════════════════════════════════════════════
 * Canvas
 * ════════════════════════════════════════════════════════════════════════ */

function EditorCanvas({
  doc, conflicts, selection, onSelect, onCommit, onSelectSimilar,
  // Viewport-relative, so a tall window shows more sheet instead of more empty
  // page below it — the other half of "I have to scroll down every time".
  height = 'min(68vh, 760px)',
}) {
  const { P, MO, TC, fmtLen } = useTheme();
  const { zoom, pan, wrapperRef, fit, beginPanDrag, eventToWorld, spaceHeld } = useCanvasControls({});

  const shW = doc?.sheet?.width || 0;
  const shH = doc?.sheet?.height || 0;
  const usable = useMemo(() => usableArea(doc), [doc]);

  // Live drag state. Kept in a ref so a mousemove does not re-render through
  // React state for the delta, and mirrored into `ghost` only when it changes
  // enough to matter on screen.
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);   // gates the listeners
  const [ghost, setGhost] = useState(null);          // { dx, dy, guides } — per frame
  // Rubber-band selection. Sheet mm, live, and null when not banding.
  const bandRef = useRef(null);
  const [band, setBand] = useState(null);

  useEffect(() => {
    if (shW > 0 && shH > 0) fit({ contentW: shW, contentH: shH, padding: 0.05, maxZoom: 4 });
  }, [shW, shH, fit]);

  /** Screen → sheet mm. The Y-flip lives here and nowhere else. */
  const toSheet = useCallback((e) => {
    const w = eventToWorld(e);
    return w ? { x: w.x, y: shH - w.y } : null;
  }, [eventToWorld, shH]);

  const pieces = useMemo(() => {
    if (!doc) return [];
    return doc.pieces.map((p) => {
      const g = pieceGeometry(doc, p);
      return g ? { piece: p, g } : null;
    }).filter(Boolean);
  }, [doc]);

  const onCanvasMouseDown = (e) => {
    // Pan is the middle button or space-drag — the idiom every design tool
    // uses, and it has to move off the plain left-drag because that is where a
    // rubber-band selection belongs. Scroll still zooms; Fit still fits.
    if (e.button === 1 || spaceHeld) { beginPanDrag(e); return; }
    if (e.button !== 0) return;
    const pt = toSheet(e);
    if (!pt) return;
    const hit = hitTest(doc, pt.x, pt.y);

    if (!hit) {
      e.preventDefault();
      // Empty sheet: band out a selection. Shift keeps what is already picked,
      // so a band can be built up in several passes.
      bandRef.current = { x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y,
        additive: e.shiftKey, base: e.shiftKey ? selection : [] };
      setBand({ ...bandRef.current });
      if (!e.shiftKey) onSelect([]);
      return;
    }

    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    let next;
    if (additive) {
      next = selection.includes(hit) ? selection.filter((u) => u !== hit) : [...selection, hit];
    } else {
      next = selection.includes(hit) ? selection : [hit];
    }
    // Double-click is the quick way to "all of these" — the same idiom as
    // select-similar everywhere else, and it costs no chrome at all.
    if (e.detail >= 2) { onSelectSimilar(hit); return; }

    onSelect(next);
    if (!next.includes(hit)) return;   // shift-clicked OUT of the selection: no drag

    e.preventDefault();
    dragRef.current = { start: pt, uids: next, moved: false };
    setGhost({ dx: 0, dy: 0, guides: [] });
    setDragging(true);
  };

  // Window-level move/up so a fast drag that leaves the canvas still tracks.
  //
  // Gated on `dragging`, NOT on `ghost`. `ghost` changes on every mousemove, so
  // depending on it would tear down and re-attach both listeners once per frame
  // — and a listener that is absent at the moment the button comes up loses the
  // mouseup, leaving a die glued to the cursor.
  // Rubber band: track to the pointer, resolve on release. Selection is
  // recomputed on every move so the operator sees what they are about to get.
  useEffect(() => {
    if (!band) return undefined;
    const onMove = (ev) => {
      const b = bandRef.current;
      if (!b) return;
      const pt = toSheet(ev);
      if (!pt) return;
      b.x1 = pt.x; b.y1 = pt.y;
      setBand({ ...b });
      onSelect(unionSelection(b.base, piecesInRect(doc, b.x0, b.y0, b.x1, b.y1)));
    };
    const onUp = () => {
      const b = bandRef.current;
      bandRef.current = null;
      setBand(null);
      if (!b) return;
      // A band with no area is a click on empty sheet: clear, as before.
      if (Math.abs(b.x1 - b.x0) < 0.5 && Math.abs(b.y1 - b.y0) < 0.5) {
        onSelect(b.additive ? b.base : []);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [band, doc, toSheet, onSelect, selection]);

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const pt = toSheet(e);
      if (!pt) return;
      const raw = { dx: pt.x - d.start.x, dy: pt.y - d.start.y };
      const snapped = e.altKey
        ? { ...raw, guides: [] }                              // Alt = place it exactly there
        : snapDelta(doc, d.uids, raw.dx, raw.dy, SNAP_PX / Math.max(zoom, 1e-6));
      d.moved = d.moved || Math.abs(raw.dx) > 0.01 || Math.abs(raw.dy) > 0.01;
      d.last = snapped;
      setGhost(snapped);
    };
    const onUp = () => {
      const d = dragRef.current;
      dragRef.current = null;
      setGhost(null);
      setDragging(false);
      if (d?.moved && d.last && (d.last.dx || d.last.dy)) onCommit(d.uids, d.last.dx, d.last.dy);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, doc, zoom, toSheet, onCommit]);

  if (!doc) return null;

  const dx = ghost?.dx || 0;
  const dy = ghost?.dy || 0;
  const sel = new Set(selection);
  const bad = conflicts.offending;

  /** MNE Y-up mm → SVG Y-down, with the live drag delta for moving pieces. */
  const toSvg = (pts, moving) => pts
    .map(([x, y]) => `${x + (moving ? dx : 0)},${shH - (y + (moving ? dy : 0))}`)
    .join(' ');

  return (
    <div style={{
      position: 'relative', height, minHeight: 400, borderRadius: 10, overflow: 'hidden',
      border: `0.5px solid ${P.rule}`, background: P.bg,
    }}>
      <DotGrid />
      <div
        ref={wrapperRef}
        onMouseDown={onCanvasMouseDown}
        style={{ position: 'absolute', inset: 0,
          cursor: spaceHeld ? 'grab' : (dragging ? 'grabbing' : (band ? 'crosshair' : 'default')) }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%"
          style={{ display: 'block', position: 'absolute', inset: 0 }}>
          <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`}>
            <rect x={0} y={0} width={shW} height={shH} fill={P.sheet} stroke={P.rule}
              strokeWidth={1} vectorEffect="non-scaling-stroke" />

            {/* Margin strips — gripper (bottom, MNE) / tail (top) / guides. */}
            {doc.sheet.margins && (() => {
              const m = doc.sheet.margins;
              const strips = [];
              if (m.bottom > 0) strips.push({ k: 'b', x: 0, y: shH - m.bottom, w: shW, h: m.bottom, label: `${fmtLen(m.bottom)} gripper` });
              if (m.top > 0) strips.push({ k: 't', x: 0, y: 0, w: shW, h: m.top, label: `${fmtLen(m.top)} tail` });
              if (m.left > 0) strips.push({ k: 'l', x: 0, y: 0, w: m.left, h: shH, label: null });
              if (m.right > 0) strips.push({ k: 'r', x: shW - m.right, y: 0, w: m.right, h: shH, label: null });
              return strips.map((s) => (
                <g key={s.k}>
                  <rect x={s.x} y={s.y} width={s.w} height={s.h} fill={P.dangerSoft}
                    stroke={P.dangerLine} strokeWidth={1} strokeDasharray="4,3"
                    vectorEffect="non-scaling-stroke" />
                  {s.label && s.h >= 6 && (
                    <text x={shW / 2} y={s.y + s.h / 2} fill={P.dangerLine}
                      fontSize={Math.min(s.h * 0.5, 8)} textAnchor="middle" dominantBaseline="central"
                      style={{ fontFamily: MO, pointerEvents: 'none' }}>{s.label}</text>
                  )}
                </g>
              ));
            })()}

            {pieces.map(({ piece, g }) => {
              const t = g.proto;
              const moving = !!ghost && sel.has(piece.uid);
              const selected = sel.has(piece.uid);
              const conflicted = bad.has(piece.uid);
              const color = TC[t.typeIndex % TC.length];
              // A tile is drawn as its real cartons; a plain die as its outline.
              const shapes = g.components.length ? g.components : [{ role: null, vertices: g.outline }];
              const cx = (g.bbox.x0 + g.bbox.x1) / 2 + (moving ? dx : 0);
              const cy = (g.bbox.y0 + g.bbox.y1) / 2 + (moving ? dy : 0);
              return (
                <g key={piece.uid} opacity={moving ? 0.75 : 1}>
                  {shapes.map((c, k) => (
                    <polygon
                      key={k}
                      points={toSvg(c.vertices, moving)}
                      fill={color + (c.role === 'B' ? 'AB' : 'D9')}
                      stroke={conflicted ? P.danger : (selected ? P.accent : P.sheet)}
                      strokeWidth={conflicted || selected ? 2 : 0.75}
                      vectorEffect="non-scaling-stroke"
                      style={{ cursor: 'move' }}
                    />
                  ))}
                  {/* Selection frame on the whole piece, so a tile reads as one
                      draggable object even though two cartons are painted. */}
                  {selected && (
                    <rect
                      x={g.bbox.x0 + (moving ? dx : 0)} y={shH - (g.bbox.y1 + (moving ? dy : 0))}
                      width={g.bbox.w} height={g.bbox.h}
                      fill="none" stroke={P.accent} strokeWidth={1} strokeDasharray="5,3"
                      vectorEffect="non-scaling-stroke" style={{ pointerEvents: 'none' }}
                    />
                  )}
                  <text x={cx} y={shH - cy} fill={P.sheet} fontWeight={700} fontSize={7}
                    textAnchor="middle" dominantBaseline="central"
                    style={{ fontFamily: MO, pointerEvents: 'none' }}>
                    {`${String(t.label).slice(0, 10)}${t.cartons > 1 ? ` ×${t.cartons}` : ''}`}
                  </text>
                </g>
              );
            })}

            {/* Snap guides — a die that jumps without saying why reads as a bug. */}
            {ghost?.guides?.map((gd, i) => (
              <line key={i}
                x1={gd.axis === 'x' ? gd.at : 0} x2={gd.axis === 'x' ? gd.at : shW}
                y1={gd.axis === 'x' ? 0 : shH - gd.at} y2={gd.axis === 'x' ? shH : shH - gd.at}
                stroke={P.accent} strokeWidth={1} strokeDasharray="3,3"
                vectorEffect="non-scaling-stroke" style={{ pointerEvents: 'none' }}
              />
            ))}

            {/* The rubber band, in sheet mm like everything else. */}
            {band && (
              <rect
                x={Math.min(band.x0, band.x1)}
                y={shH - Math.max(band.y0, band.y1)}
                width={Math.abs(band.x1 - band.x0)}
                height={Math.abs(band.y1 - band.y0)}
                fill={P.accentSoft} stroke={P.accent} strokeWidth={1}
                strokeDasharray="4,3" vectorEffect="non-scaling-stroke"
                style={{ pointerEvents: 'none' }}
              />
            )}

            {/* Usable area — where a die is actually allowed to be. */}
            <rect x={usable.x0} y={shH - usable.y1}
              width={Math.max(0, usable.x1 - usable.x0)} height={Math.max(0, usable.y1 - usable.y0)}
              fill="none" stroke={P.accentLine} strokeWidth={1} strokeDasharray="2,4"
              vectorEffect="non-scaling-stroke" style={{ pointerEvents: 'none' }} />
            <rect x={0} y={0} width={shW} height={shH} fill="none" stroke={P.ink}
              strokeWidth={1} vectorEffect="non-scaling-stroke" style={{ pointerEvents: 'none' }} />
          </g>
        </svg>

        <SheetChrome ox={pan.x} oy={pan.y} scale={zoom} sheetW={shW} sheetH={shH} />
      </div>

      <button
        onClick={() => fit({ contentW: shW, contentH: shH, padding: 0.05, maxZoom: 4 })}
        style={{
          position: 'absolute', right: 10, bottom: 10, fontFamily: MO, fontSize: 10,
          padding: '4px 10px', borderRadius: 4, background: P.elev, color: P.ink,
          border: `0.5px solid ${P.rule}`, cursor: 'pointer',
        }}>Fit</button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * Screen
 * ════════════════════════════════════════════════════════════════════════ */

export default function LayoutEditor({
  state, setState, onExport, exporting = false, exportError = null,
  // The "Remove carton borders" setting is App's — one choice for both download
  // buttons — and `borderReport` is what came back from the last export.
  removeBorders = true, onRemoveBordersChange = null, borderReport = null,
  // 1.11: the marks and bleed switches (one setting in App, like the borders
  // switch) and the report from the last export.
  pressSwitches = null, pressReport = null,
  availableSourceIds = null, onGoToLayout, onRequestPrepressTile = null,
  // The editor's desk. `onAddCarton` opens the SAME add-carton overlay Layout &
  // Export uses (upload, library, die detection, colour preflight) — there is
  // no second add flow here and there should not be one. `onRemoveCarton` takes
  // a carton off the desk by source_id; App drops the bytes and the editor
  // drops the prototype and its pieces, which have to happen together.
  onAddCarton = null, onRemoveCarton = null,
  // Deployment switch (capabilities.editor_custom_pair_arrangement). Off by
  // default: measured on the real corpus, "across" never interlocks and the
  // turn changes nothing on most cartons, so the controls mostly offer a choice
  // that does not change the die. Prepress's own tile is then the only offer.
  allowCustomPairArrangement = false,
}) {
  const { P, FN, MO, DISPLAY, inp, unit, fmtLen } = useTheme();
  const [selection, setSelection] = useState([]);
  const [addRot, setAddRot] = useState(0);
  // Which set-once cards are open. Closed by default: Sheet and Margins are
  // read or set at the START of a session and then never again, so they cost
  // one heading each until wanted. The DOC still owns every value — this is
  // only what is on screen, so nothing here can change what gets cut.
  const [openCard, setOpenCard] = useState({ sheet: false, margins: false, keys: false });
  const toggleCard = useCallback((k) => setOpenCard((o) => ({ ...o, [k]: !o[k] })), []);
  // How the Add palette places a carton: one die, or a hand-built pair of two.
  const [addMode, setAddMode] = useState('single');   // 'single' | 'pair'
  const [tileAxis, setTileAxis] = useState('x');      // across | up
  const [tileTurn, setTileTurn] = useState(180);      // second copy's turn
  const [tileBusy, setTileBusy] = useState(null);     // proto key being fetched
  const [tileError, setTileError] = useState(null);
  // Which margin editor is showing. The DOC holds the four edges; this is only
  // the view, seeded from whether the loaded layout's edges already differ.
  const [marginMode, setMarginMode] = useState(
    () => (marginsAreUniform(state?.doc?.sheet?.margins) ? 'uniform' : 'perSide'));

  const doc = state?.doc || null;

  const railField = { flex: 1, fontFamily: MO, fontSize: 10.5, color: P.muted, display: 'block' };
  const btn = (tone = 'plain') => ({
    fontFamily: MO, fontSize: 11, padding: '6px 11px', borderRadius: 5, cursor: 'pointer',
    background: tone === 'solid' ? P.ink : 'transparent',
    color: tone === 'solid' ? P.sheet : (tone === 'danger' ? P.danger : P.muted),
    border: `0.5px solid ${tone === 'danger' ? P.dangerLine : P.rule}`, whiteSpace: 'nowrap',
  });

  const commit = useCallback((next, coalesce = null) => {
    setState((s) => editorApply(s, next, { coalesce }));
  }, [setState]);

  const conflicts = useMemo(
    () => (doc ? computeConflicts(doc) : { overlaps: [], tooClose: [], outOfBounds: [], offending: new Set() }),
    [doc],
  );
  const stats = useMemo(() => (doc ? docStats(doc) : null), [doc]);

  // The projected result, computed once — the export payload, the artwork check
  // and (below) the blocking rules all read the same projection.
  const projected = useMemo(() => (doc ? docToVectorResult(doc) : null), [doc]);
  // Cartons whose prepress chain cannot be reduced to one placement. Surfaced
  // here rather than thrown at click time: the operator should know the sheet
  // is unplatable while they can still do something about it.
  const unplaceable = useMemo(
    () => (projected ? unplaceableCartons(projected) : []),
    [projected],
  );

  /**
   * What the Add palette offers, with the real footprint of what would land.
   *
   * In pair mode the pair prototype is BUILT here (against a throwaway doc) so
   * the button can show the true interlocked size before anything is committed.
   * `gain` is how much narrower the interlock is than the same two dies placed
   * apart — reported only when the die actually nests, because for a
   * straight-sided carton the honest answer is "nothing", and a badge claiming
   * otherwise on every carton would teach the operator to ignore it.
   *
   * Synthesized pairs are hidden from the list: they are reached through the
   * mode switch, and showing them again would offer a pair of a pair.
   */
  const palette = useMemo(() => {
    if (!doc) return [];
    const orphaned = (t) => !!(availableSourceIds && t.sourceId && !availableSourceIds.includes(t.sourceId));
    const report = { prepress_report: doc.prepressReport };
    return doc.prototypes
      .filter((t) => t.origin !== 'tile' && t.origin !== 'prepress-tile')
      .filter((t) => (addMode === 'pair' ? isTileable(t) : true))
      .map((t) => {
        const cartonType = cartonTypeFor(report, t.sourceId);
        // How many of this carton are ALREADY down. The palette is the desk's
        // one view, and "have I placed this one yet" is the question an
        // operator laying out a sheet by hand asks between every click — with
        // eight rows of similar names it is not answerable from the canvas.
        // Counted by SOURCE, so a carton's singles and its pairs count as the
        // same carton, exactly as selecting them does.
        const placed = t.sourceId ? piecesOfSource(doc, t.sourceId).length : 0;
        if (addMode !== 'pair') {
          return { proto: t, size: rotatedSize(t, addRot), orphan: orphaned(t), gain: 0, cartonType, placed };
        }
        // A carton prepress owns: it decides the arrangement, so there is
        // nothing to preview and nothing for the operator to choose. Showing a
        // hand-built pair's footprint here would advertise a die we are not
        // going to build.
        // A self-mated family (RTE / STE) uses the same blank for both halves,
        // so the operator may arrange it themselves as well. A fused family
        // (BL / IL) may not — the resequencing is the tile.
        // A self-mated family COULD take a hand-arranged pair; whether we offer
        // one is the deployment's call, because without the arrangement
        // controls the custom pair has nothing to offer over prepress's tile.
        const custom = offersCustomPair(cartonType, allowCustomPairArrangement);
        const { doc: probe, key } = ensureTilePrototype(doc, t.key, { axis: tileAxis, turn: tileTurn });
        const pair = key ? probe.prototypes.find((q) => q.key === key) : null;
        const along = tileAxis === 'x' ? 'w' : 'h';
        const apart = t[along] * 2 + doc.sheet.kerf;
        const customInfo = (custom && pair) ? {
          customSize: rotatedSize(pair, addRot),
          gain: apart > 0 ? Math.max(0, (apart - pair[along]) / apart) : 0,
        } : { customSize: null, gain: 0 };

        if (cartonType && onRequestPrepressTile) {
          const made = hasPrepressTile(doc, t.sourceId)
            ? protoOf(doc, prepressTileKey(t.sourceId)) : null;
          return {
            proto: t, orphan: orphaned(t), cartonType, prepress: true, custom, placed,
            size: made ? rotatedSize(made, addRot) : null,
            ...customInfo,
          };
        }
        if (!pair) return { proto: t, size: rotatedSize(t, addRot), orphan: orphaned(t), gain: 0, cartonType, custom, placed };
        return {
          proto: t,
          size: customInfo.customSize,
          orphan: orphaned(t),
          cartonType,
          custom,
          placed,
          gain: customInfo.gain,
        };
      });
  }, [doc, addMode, addRot, tileAxis, tileTurn, availableSourceIds,
      onRequestPrepressTile, allowCustomPairArrangement]);

  // Prune a selection that an undo or a delete has invalidated, so the
  // inspector never edits a piece that is no longer on the sheet.
  useEffect(() => {
    if (!doc) return;
    const live = new Set(doc.pieces.map((p) => p.uid));
    setSelection((prev) => (prev.every((u) => live.has(u)) ? prev : prev.filter((u) => live.has(u))));
  }, [doc]);

  /* ── Operations ──────────────────────────────────────────────────────── */

  /**
   * Nudge, reading the document out of STATE rather than the closure.
   *
   * The pad's buttons repeat while held and key auto-repeat fires faster than
   * React re-renders, so a handler that closed over `doc` would compute every
   * step from the same stale document: the piece would move one step and then
   * sit there while the button was held down, which looks like a dead control
   * rather than a bug.
   *
   * Coalesced, so a burst of repeats is one undo step and not forty — at 55 ms
   * a repeat, holding a button for two seconds would otherwise bury the history
   * limit and throw away everything before it.
   */
  const moveSel = useCallback((dx, dy) => {
    if (!selection.length) return;
    setState((st) => (st?.doc
      ? editorApply(st, movePieces(st.doc, selection, dx, dy), { coalesce: 'nudge' })
      : st));
  }, [selection, setState]);

  /**
   * Rotate BY an angle, reading the document out of STATE rather than the
   * closure — for the same reason moveSel does.
   *
   * This one bit. The ring emits an incremental delta on every mousemove, and
   * mousemove fires far faster than React re-renders, so a handler closing over
   * `doc` applied every delta to the SAME stale document: each call replaced the
   * last instead of adding to it, and only the final fractional delta survived.
   * The ring appeared dead for a multi-selection.
   *
   * The single-selection path escaped it only by luck — it sends an ABSOLUTE
   * angle, which is idempotent against a stale base — which is exactly why the
   * bug looked like "the ring does not work with multiple cartons" rather than
   * "the ring does not work".
   *
   * `coalesce` is passed by the caller: a drag is one undo step, a button press
   * is its own.
   */
  const rotateSel = useCallback((by, coalesce = 'rotate.dial') => {
    if (!selection.length) return;
    setState((st) => (st?.doc
      ? editorApply(st, rotatePieces(st.doc, selection, { by }), { coalesce })
      : st));
  }, [selection, setState]);

  /** Rotate TO an absolute angle. Single selection only — several pieces at
   *  different angles have no common angle to set. */
  const rotateSelTo = useCallback((deg) => {
    if (selection.length !== 1) return;
    setState((st) => (st?.doc
      ? editorApply(st, rotatePieces(st.doc, selection, { to: normalizeAngle(deg) }),
        { coalesce: 'rotate.dial' })
      : st));
  }, [selection, setState]);

  /** Every piece cut from the same upload as `uid`. */
  const selectSameCarton = useCallback((uid) => {
    if (!doc) return;
    const sid = protoOf(doc, doc.pieces.find((p) => p.uid === uid))?.sourceId;
    const all = piecesOfSource(doc, sid);
    if (all.length) setSelection(all);
  }, [doc]);


  const deleteSel = useCallback(() => {
    if (!doc || !selection.length) return;
    commit(removePieces(doc, selection));
    setSelection([]);
  }, [doc, selection, commit]);

  const duplicateSel = useCallback(() => {
    if (!doc || !selection.length) return;
    const { doc: next, uids } = duplicatePieces(doc, selection, DUP_OFFSET, -DUP_OFFSET);
    commit(next);
    setSelection(uids);
  }, [doc, selection, commit]);

  /**
   * @param how  'prepress' asks the package for the carton's own tile;
   *             'custom' slides two copies together in the arrangement the
   *             operator picked. Passed explicitly rather than inferred, so a
   *             row offering both cannot do the wrong one.
   */
  const addFromPalette = useCallback(async (protoKey, how = 'prepress') => {
    if (!doc) return;
    setTileError(null);
    const proto = protoOf(doc, protoKey);

    if (addMode === 'pair') {
      // A carton prepress knows how to build gets its REAL tile, from prepress.
      // A BL tile resequences the panels of one half under rules that live in
      // the package — sliding two dies together here would produce a different
      // die from the one the press would cut.
      const cartonType = proto && cartonTypeFor({ prepress_report: doc.prepressReport }, proto.sourceId);
      if (how === 'prepress' && cartonType && onRequestPrepressTile) {
        // Already fetched once this session — reuse it rather than asking again.
        if (hasPrepressTile(doc, proto.sourceId)) {
          const key = prepressTileKey(proto.sourceId);
          const spot = findFreeSpot(doc, key, addRot);
          const next = addPiece(doc, key, { ...spot, rot: addRot });
          commit(next);
          setSelection([next.pieces[next.pieces.length - 1].uid]);
          return;
        }
        const src = (doc.sources || []).find((sv) => sv?.source_id === proto.sourceId);
        if (!src?.vertices?.length) {
          setTileError(`No die outline on record for ${proto.label}, so prepress cannot be asked to tile it.`);
          return;
        }
        setTileBusy(protoKey);
        try {
          const entry = reportEntryFor({ prepress_report: doc.prepressReport }, proto.sourceId);
          const tile = await onRequestPrepressTile({
            sourceId: proto.sourceId,
            vertices: src.vertices,
            cartonType,
            // The job's own config, so the tile's halves are the same die as
            // the singles already on the sheet.
            prepressConfig: tileConfigFrom(entry),
          });
          const { doc: next, uid } = addPrepressTile(doc, tile, { rot: addRot });
          if (!uid) {
            setTileError('Prepress returned a tile that could not be read.');
            return;
          }
          commit(next);
          setSelection([uid]);
        } catch (e) {
          setTileError(e?.message || String(e));
        } finally {
          setTileBusy(null);
        }
        return;
      }

      // Either prepress cannot build this one, or the operator asked for their
      // own arrangement on a self-mated family. Both halves are the same blank
      // in that case, so this is the die the press would cut — placed by them.
      const { doc: next, uid } = addTile(doc, protoKey, { axis: tileAxis, turn: tileTurn, rot: addRot });
      if (!uid) return;
      commit(next);
      setSelection([uid]);
      return;
    }
    const spot = findFreeSpot(doc, protoKey, addRot);
    const next = addPiece(doc, protoKey, { ...spot, rot: addRot });
    commit(next);
    setSelection([next.pieces[next.pieces.length - 1].uid]);
  }, [doc, addRot, addMode, tileAxis, tileTurn, commit, onRequestPrepressTile]);

  /**
   * Take a carton off the desk.
   *
   * Two things have to go together and neither is optional: the PDF BYTES
   * (App's list — without them the carton is unplateable, and a palette row
   * that cannot plate is worse than no row) and the PROTOTYPE with every piece
   * it put on the sheet (this document — a piece whose prototype is gone
   * renders as nothing and is checked by nothing, which is a die on the plate
   * that no one can see).
   *
   * Pieces already placed are a real loss, so they are counted and confirmed
   * rather than swept. It is one undo step either way.
   */
  const removeFromDesk = useCallback((sourceId) => {
    if (!doc || !sourceId) return;
    const placed = piecesOfSource(doc, sourceId).length;
    if (placed > 0) {
      const ok = window.confirm(
        `${sourceId} has ${placed} piece${placed > 1 ? 's' : ''} on the sheet.\n\n`
        + 'Removing it from the desk takes them off too. Continue?');
      if (!ok) return;
    }
    commit(removeSourceCarton(doc, sourceId));
    if (onRemoveCarton) onRemoveCarton(sourceId);
  }, [doc, commit, onRemoveCarton]);

  const undo = useCallback(() => setState((s) => editorUndo(s)), [setState]);
  const redo = useCallback(() => setState((s) => editorRedo(s)), [setState]);

  /* ── Keyboard ────────────────────────────────────────────────────────── */

  useEffect(() => {
    if (!doc) return undefined;
    const onKey = (e) => {
      if (isTypingTarget(e.target)) return;
      const mod = e.metaKey || e.ctrlKey;
      const key = (e.key || '').toLowerCase();

      if (mod && key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }
      if (mod && key === 'd') { e.preventDefault(); duplicateSel(); return; }
      if (mod && key === 'a') {
        e.preventDefault();
        setSelection(doc.pieces.map((p) => p.uid));
        return;
      }
      if (e.key === 'Escape') { setSelection([]); return; }
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); deleteSel(); return; }
      if (key === 'r') { e.preventDefault(); rotateSel(e.shiftKey ? -90 : 90, null); return; }

      const step = e.shiftKey ? NUDGE.coarse : (e.altKey ? NUDGE.fine : NUDGE.normal);
      const nudges = {
        ArrowLeft: [-step, 0], ArrowRight: [step, 0],
        // Screen-up is sheet-Y-up: the canvas Y-flip does not reach the keys.
        ArrowUp: [0, step], ArrowDown: [0, -step],
      };
      if (nudges[e.key]) { e.preventDefault(); moveSel(...nudges[e.key]); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doc, undo, redo, duplicateSel, deleteSel, rotateSel, moveSel]);

  // A carton on the sheet whose PDF is no longer on the bench cannot be
  // plated. Say so here rather than letting the export 400 at the very end.
  const missingArt = useMemo(() => (doc && availableSourceIds
    ? Array.from(new Set(
      doc.pieces.map((p) => protoOf(doc, p)?.sourceId)
        .filter((sid) => sid && !availableSourceIds.includes(sid)),
    ))
    : []), [doc, availableSourceIds]);

  // Two different sentences hide inside `tooClose`: a gap between two pieces,
  // and a gap INSIDE a hand-built pair whose kerf was raised after it was made.
  // The fix differs (move one, versus rebuild the pair), so they are told apart.
  const tightBetween = useMemo(
    () => conflicts.tooClose.filter(([a, b]) => a !== b), [conflicts]);
  const tightWithin = useMemo(
    () => conflicts.tooClose.filter(([a, b]) => a === b), [conflicts]);

  /**
   * Everything wrong with the sheet, as one ordered list, most severe first.
   *
   * Built here rather than inline in the panel because the panel now lives ON
   * the canvas and the same list drives both its collapsed summary and its
   * expanded rows — two renderings of one truth, not two lists that can
   * disagree about how many problems there are.
   */
  const issues = useMemo(() => {
    const out = [];
    if (!doc) return out;
    const uniq = (pairs) => Array.from(new Set(pairs.flat()));
    if (conflicts.overlaps.length) {
      out.push({ key: 'overlap', tone: 'danger',
        label: `${conflicts.overlaps.length} overlap${conflicts.overlaps.length > 1 ? 's' : ''}`,
        detail: 'Dies share area — the press cannot cut this.',
        uids: uniq(conflicts.overlaps) });
    }
    if (unplaceable.length) {
      out.push({ key: 'unplaceable', tone: 'danger', label: 'Artwork cannot be placed',
        detail: `${unplaceable.map((u) => `${u.name}: ${u.reason}`).join(' · ')}. `
          + 'Re-run the layout with a prepress package that publishes the transform chain.' });
    }
    if (missingArt.length) {
      out.push({ key: 'missing', tone: 'danger', label: 'Artwork missing',
        detail: `No PDF on the bench for: ${missingArt.join(', ')}.` });
    }
    if (tightBetween.length) {
      out.push({ key: 'tight', tone: 'warn', label: `${tightBetween.length} under kerf`,
        detail: `Two dies closer than the ${fmtLen(doc.sheet.kerf)} knife clearance.`,
        uids: uniq(tightBetween) });
    }
    if (tightWithin.length) {
      out.push({ key: 'pairtight', tone: 'warn', label: `${tightWithin.length} pair too tight`,
        detail: `Built for a smaller kerf than the ${fmtLen(doc.sheet.kerf)} now set. `
          + 'Remove and re-add the pair to re-solve its interlock.',
        uids: uniq(tightWithin) });
    }
    if (conflicts.outOfBounds.length) {
      // DANGER, not warn. It blocks the export (see `exportBlockReason`), and a
      // sheet that shows an amber advisory next to a dead button reads as a
      // broken button rather than a refused sheet.
      out.push({ key: 'bounds', tone: 'danger',
        label: `${conflicts.outOfBounds.length} out of bounds`,
        detail: 'Crosses a gripper/tail/guide strip or the sheet edge.',
        uids: conflicts.outOfBounds });
    }
    return out;
  }, [conflicts, unplaceable, missingArt, tightBetween, tightWithin, doc, fmtLen]);

  /**
   * The "select all of this kind" offers for the current selection.
   *
   * Built from what is selected, not from the whole sheet: a menu listing every
   * carton on the job would be a carton picker, and the operator has already
   * said which one they mean by clicking it. An offer that would not change the
   * selection is left out rather than shown greyed — a menu entry that does
   * nothing is worse than one that is missing.
   */
  const similarGroups = useMemo(() => {
    if (!doc || !selection.length) return [];
    const picked = new Set(selection);
    const protos = selection
      .map((uid) => protoOf(doc, doc.pieces.find((p) => p.uid === uid)))
      .filter(Boolean);
    const all = selectableGroups(doc);
    const out = [];
    const sids = new Set(protos.map((t) => t.sourceId).filter(Boolean));
    for (const g of all.sources) {
      if (!sids.has(g.id)) continue;
      const uids = piecesOfSource(doc, g.id);
      if (uids.length <= picked.size && uids.every((u) => picked.has(u))) continue;
      out.push({ key: `s:${g.id}`, kind: 'source', id: g.id,
        label: `All “${g.label}”`, pieces: g.pieces });
    }
    const types = new Set(protos.map((t) => t.cartonType).filter(Boolean));
    for (const g of all.types) {
      if (!types.has(g.id)) continue;
      const uids = piecesOfCartonType(doc, g.id);
      if (uids.length <= picked.size && uids.every((u) => picked.has(u))) continue;
      out.push({ key: `t:${g.id}`, kind: 'type', id: g.id,
        label: `All ${g.id} cartons`, pieces: g.pieces });
    }
    return out;
  }, [doc, selection]);

  /* ── Empty state ─────────────────────────────────────────────────────── */

  if (!doc) {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: P.accent, marginBottom: 4 }}>Manual</div>
          <h2 style={{ fontFamily: DISPLAY, fontSize: 26, fontWeight: 400, color: P.ink, margin: '0 0 12px', paddingBottom: 8, borderBottom: `0.5px solid ${P.rule}`, letterSpacing: '-0.01em' }}>Layout Editor</h2>
        </div>
        <div style={{
          height: 420, borderRadius: 10, border: `0.5px dashed ${P.rule}`, background: P.sheet,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 12, textAlign: 'center', padding: 24,
        }}>
          <div style={{ fontFamily: DISPLAY, fontSize: 24, color: P.muted }}>An empty desk</div>
          {/* Two doors, and the second one used to be missing. "Open in editor"
              was the ONLY way in, so an operator who wanted to place two dies
              by hand had to add them in Auto Imposer, run a nest they did
              not want, and open its result. Adding a carton here starts a sheet
              at the size the Layout rail is set to. */}
          <div style={{ fontFamily: MO, fontSize: 11, color: P.dim, maxWidth: 400, lineHeight: 1.6 }}>
            Add a carton to start a sheet by hand — or run a layout in Auto Imposer and
            bring it here with <strong style={{ color: P.muted }}>Open in editor</strong>,
            which copies its cartons onto this desk.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
            {onAddCarton && (
              <button onClick={onAddCarton} style={{ ...btn('solid'), padding: '8px 16px' }}>
                + Add carton
              </button>
            )}
            <button onClick={onGoToLayout} style={{ ...btn(), padding: '8px 16px' }}>
              Go to Auto Imposer
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Derived view data ───────────────────────────────────────────────── */

  const selPieces = doc.pieces.filter((p) => selection.includes(p.uid));
  const one = selPieces.length === 1 ? selPieces[0] : null;
  const oneGeo = one ? pieceGeometry(doc, one) : null;
  const oneProto = one ? protoOf(doc, one) : null;

  const issueCount = conflicts.overlaps.length + conflicts.tooClose.length + conflicts.outOfBounds.length;

  // ONE rule for "may this sheet be exported", stated in editorDoc so it can be
  // tested — nothing in this repo renders JSX. It replaces a boolean here and a
  // separate tooltip ternary on the button, which had already drifted apart:
  // an empty sheet disabled the button while the tooltip still offered to gang
  // it, and an OUT-OF-BOUNDS die disabled nothing at all even though the sheet
  // counted it as an issue and drew it in warn on the canvas.
  const blockReason = exportBlockReason({
    conflicts, missingArt, unplaceable, pieceCount: doc.pieces.length,
  });
  const blocked = blockReason !== null;

  const SHEET_PRESETS = [{ l: '28×20', w: 711, h: 508 }, { l: '26×20', w: 660, h: 508 }, { l: 'B2', w: 707, h: 500 }];
  const m = doc.sheet.margins || ZERO_MARGINS;
  const uniformMargin = marginsAreUniform(m) ? m.bottom : null;
  // The same collapse rule the backend enforces (422) and Auto Imposer
  // checks — stated here too, because the editor can resize the sheet UNDER a
  // set of margins and reach the invalid state from the other direction.
  const marginError = marginCollapseError(m, doc.sheet.width, doc.sheet.height, fmtLen);

  // What a folded card says instead of nothing. A fold that hides a number the
  // operator still needs would be worse than the scroll it replaces, so the
  // heading carries the value and the card carries the controls.
  const sheetSummary = `${fmtLen(doc.sheet.width)} × ${fmtLen(doc.sheet.height)}`
    + (doc.sheet.kerf ? ` · kerf ${fmtLen(doc.sheet.kerf)}` : '');
  const marginSummary = marginError ? 'does not fit — open to fix'
    : (uniformMargin !== null
      ? (uniformMargin ? `${fmtLen(uniformMargin)} all round` : 'edge to edge')
      : 'per side');

  return (
    <div>
      <div style={{ marginBottom: 18, display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: P.accent, marginBottom: 4 }}>Manual</div>
          <h2 style={{ fontFamily: DISPLAY, fontSize: 26, fontWeight: 400, color: P.ink, margin: '0 0 12px', paddingBottom: 8, borderBottom: `0.5px solid ${P.rule}`, letterSpacing: '-0.01em' }}>Layout Editor</h2>
        </div>
        <div style={{ display: 'flex', gap: 6, paddingBottom: 14 }}>
          <button onClick={undo} disabled={!state.past.length} title="Undo (⌘Z)"
            style={{ ...btn(), opacity: state.past.length ? 1 : 0.4, cursor: state.past.length ? 'pointer' : 'default' }}>↶ Undo</button>
          <button onClick={redo} disabled={!state.future.length} title="Redo (⇧⌘Z)"
            style={{ ...btn(), opacity: state.future.length ? 1 : 0.4, cursor: state.future.length ? 'pointer' : 'default' }}>↷ Redo</button>
        </div>
      </div>

      <div className="mono-twopane" style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 300px) 1fr', gap: 16, alignItems: 'start' }}>

        {/* ── RAIL ── */}
        {/* THE RAIL SCROLLS INSIDE ITSELF, and is ordered by how often a
            control is touched — not by the order a job is set up in.

            It used to be five always-open cards running Sheet, Margins, Add
            carton, Export, Shortcuts. Sheet and Margins are set ONCE per
            session; the palette and Export are used constantly. So the two
            constant controls sat roughly 600px down, under two set-once ones,
            and the palette — the tallest card and the most-clicked control in
            the app — was the one that fell below the fold. Reaching it meant
            scrolling the CANVAS off screen, clicking blind, and scrolling back
            to see where the die landed.

            Pinning is safe here: the only overflow:hidden in the chain is
            EditorCanvas's own container, which is the sibling pane, not an
            ancestor — and the grid at the parent already sets alignItems:start,
            which is the precondition a sticky grid item needs. */}
        <aside className="mono-rail" style={{
          display: 'flex', flexDirection: 'column', gap: 14,
          position: 'sticky', top: RAIL_TOP,
          maxHeight: `calc(100vh - ${RAIL_TOP + RAIL_BOTTOM}px)`,
          overflowY: 'auto', overscrollBehavior: 'contain',
        }}>
          <RailSection title="Cartons">

            {/* THE DESK. The editor keeps its own — cartons arrive here either
                by "Open in editor" (which copies the Layout bench across) or
                straight from this button, which opens the SAME overlay the
                Layout rail does: upload a PDF or pick from the library, die
                detection and colour preflight included. Reusing it is the whole
                point; a second add flow would be a second place for the
                detection contract to drift. */}
            {onAddCarton && (
              <>
                <button onClick={onAddCarton} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
                  cursor: 'pointer', fontFamily: MO, fontSize: 12, padding: '9px 12px', borderRadius: 6,
                  background: P.elev, border: `0.5px dashed ${P.rule}`, color: P.ink, marginBottom: 10,
                }}>
                  + Add carton
                </button>
                {/* Said once, here, because it is the one way this desk differs
                    from the nest's: prepress has not run on a carton dropped
                    straight in, so what gets placed is the die that was
                    detected. For a family whose flaps prepress cuts, the
                    flapped blank still comes in through Auto Imposer. */}
                <div style={{ fontFamily: FN, fontSize: 10, color: P.dim, marginBottom: 10, lineHeight: 1.45 }}>
                  A carton added here is placed as its <strong style={{ color: P.muted }}>detected dieline</strong>.
                  Cartons whose flaps prepress cuts arrive with them through Auto Imposer.
                </div>
              </>
            )}

            {/* One die, or two of the same die interlocked into a pair. */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
              {[['single', 'Single'], ['pair', 'Tile ×2']].map(([k, lbl]) => (
                <button key={k} onClick={() => setAddMode(k)} style={{
                  flex: 1, fontFamily: MO, fontSize: 10.5, padding: '5px 8px', borderRadius: 5, cursor: 'pointer',
                  background: addMode === k ? P.ink : 'transparent', color: addMode === k ? P.sheet : P.muted,
                  border: `0.5px solid ${addMode === k ? P.ink : P.rule}`,
                }}>{lbl}</button>
              ))}
            </div>

            {/* These apply to the CUSTOM pair only — the ⧉ action on a row, and
                the whole button on a carton prepress does not know.
                A self-mated family (RTE / STE) uses one blank for both halves,
                so an arrangement the operator picks is a die the press can cut.
                A fused family (BL / IL) resequences panels, and prepress's own
                tile is the only correct answer there — those rows offer no ⧉,
                so nothing here reaches them. */}
            {/* GATED ON THE SWITCH ITSELF, not merely on "some row can take a
                custom pair". `offersCustomPair(null, …)` is true for a carton
                prepress does not recognise — correctly, since a hand-built
                pair is the only pair such a carton can have — so one untyped
                row put these controls back on screen with the deployment
                switch off, which is exactly what it was turned off to prevent.
                The switch is about the ARRANGEMENT CHOICE; pairing itself is
                unaffected, and with the choice hidden a pair is built with the
                measured defaults (across, turned 180°). */}
            {/* Rotation sits ABOVE the palette because it is read BEFORE the
                click that uses it: a palette row places a die AT `addRot`, so a
                control found underneath the list is found one die too late. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontFamily: MO, fontSize: 10, color: P.dim }}>Place at</span>
              {[0, 90, 180, 270].map((r) => (
                <button key={r} onClick={() => setAddRot(r)} style={{
                  fontFamily: MO, fontSize: 10, padding: '3px 7px', borderRadius: 4, cursor: 'pointer',
                  background: addRot === r ? P.ink : 'transparent', color: addRot === r ? P.sheet : P.muted,
                  border: `0.5px solid ${addRot === r ? P.ink : P.rule}`,
                }}>{r}°</button>
              ))}
            </div>
            {addMode === 'pair' && allowCustomPairArrangement
              && palette.some((e) => e.custom) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 10 }}>
                <Choice label="Arrange" value={tileAxis} onChange={setTileAxis}
                  options={[['x', 'Across'], ['y', 'Stacked']]} />
                <Choice label="Second" value={tileTurn} onChange={setTileTurn}
                  options={[[180, 'Turned 180°'], [0, 'Same way']]} />
              </div>
            )}

            {tileError && (
              <div style={{
                marginBottom: 10, padding: '9px 11px', borderRadius: 6,
                background: P.dangerSoft, border: `0.5px solid ${P.dangerLine}`,
                fontFamily: FN, fontSize: 11, color: P.ink, whiteSpace: 'pre-line',
              }}>{tileError}</div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {palette.map(({ proto: t, size, orphan, gain, cartonType, prepress,
                custom, customSize, placed }) => {
                const busy = tileBusy === t.key;
                // A row offers the secondary "arrange it myself" action only
                // when BOTH are available; when prepress cannot build the
                // carton, the main button already is the custom pair.
                const showCustom = addMode === 'pair' && prepress && custom && !orphan;
                return (
                  <div key={t.key} style={{ display: 'flex', gap: 4, alignItems: 'stretch' }}>
                  <button onClick={() => addFromPalette(t.key, 'prepress')}
                    disabled={orphan || !!tileBusy}
                    title={orphan
                      ? `No PDF on the bench for ${t.sourceId} — it could be placed but not plated`
                      : (addMode === 'pair'
                        ? (prepress
                          ? `Ask prepress to build the ${cartonType} tile for ${t.label} — `
                            + 'it fuses two blanks and resequences the panels, which only prepress can do'
                          : `Add a pair of ${t.label} (${fmtLen(size.w)} × ${fmtLen(size.h)})`
                            + (gain > 0.005 ? ` — interlocks, saving ${(gain * 100).toFixed(0)}% over two apart` : ''))
                        : `Add ${t.label} (${fmtLen(size.w)} × ${fmtLen(size.h)})`)}
                    style={{
                      // `flex: 1, minWidth: 0` and NOT `width: 100%`. As a flex
                      // item, 100% is a BASIS the row cannot shrink below, so
                      // every sibling control — ⧉ and × — was pushed past the
                      // rail's right edge and out of reach, with a horizontal
                      // scrollbar as the only clue. minWidth:0 is the other
                      // half: without it the ellipsised label sets a floor of
                      // its own and the row still overflows.
                      display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                      flex: 1, minWidth: 0,
                      fontFamily: MO, fontSize: 11, padding: '7px 10px', borderRadius: 5,
                      cursor: (orphan || tileBusy) ? 'not-allowed' : 'pointer',
                      opacity: orphan ? 0.45 : (tileBusy && !busy ? 0.5 : 1),
                      background: P.elev, color: P.ink, border: `0.5px solid ${P.rule}`,
                    }}>
                    <span style={{ color: P.accent }}>{busy ? '…' : '+'}</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.label}</span>
                    {(addMode === 'pair' || t.cartons > 1) && (
                      <span style={{ fontSize: 9, color: P.accent, border: `0.5px solid ${P.accentLine}`, borderRadius: 3, padding: '1px 4px' }}>
                        ×{addMode === 'pair' ? 2 : t.cartons}
                      </span>
                    )}
                    {/* Prepress owns this carton's tile. Say so, so it is clear
                        the die comes from the package and not from here. */}
                    {addMode === 'pair' && prepress && (
                      <span style={{ fontSize: 9, color: P.muted, border: `0.5px solid ${P.rule}`, borderRadius: 3, padding: '1px 4px' }}>
                        {busy ? 'building…' : cartonType}
                      </span>
                    )}
                    {/* Only shown when the interlock actually buys something —
                        a straight-sided die pairs to exactly two side by side,
                        and claiming a saving there would be a lie. */}
                    {addMode === 'pair' && !prepress && gain > 0.005 && (
                      <span title="Narrower than two placed apart, because the die nests into its turned twin"
                        style={{ fontSize: 9, color: P.ok, border: `0.5px solid ${P.okLine}`, borderRadius: 3, padding: '1px 4px' }}>
                        −{(gain * 100).toFixed(0)}%
                      </span>
                    )}
                    {placed > 0 && (
                      <span title={`${placed} on the sheet`}
                        style={{ fontSize: 9, color: P.muted, border: `0.5px solid ${P.rule}`, borderRadius: 3, padding: '1px 4px' }}>
                        {placed} down
                      </span>
                    )}
                    <span style={{ fontSize: 9.5, color: P.dim }}>
                      {size ? `${fmtLen(size.w)}×${fmtLen(size.h)}` : '—'}
                    </span>
                  </button>
                  {showCustom && (
                    <button onClick={() => addFromPalette(t.key, 'custom')}
                      disabled={!!tileBusy}
                      title={`Arrange a pair of ${t.label} yourself instead — `
                        + `${tileAxis === 'y' ? 'stacked' : 'across'}, `
                        + `second ${tileTurn ? `turned ${tileTurn}°` : 'the same way'}`
                        + (customSize ? ` (${fmtLen(customSize.w)} × ${fmtLen(customSize.h)})` : '')
                        + `. ${cartonType} halves are the same blank, so this is a die the press can cut.`}
                      style={{
                        fontFamily: MO, fontSize: 12, padding: '0 9px', borderRadius: 5,
                        cursor: tileBusy ? 'not-allowed' : 'pointer', flexShrink: 0,
                        background: 'transparent', color: P.muted,
                        border: `0.5px solid ${P.rule}`,
                      }}>⧉</button>
                  )}
                  {/* Off the desk — bytes, prototype and pieces together. */}
                  {onRemoveCarton && t.sourceId && (
                    <button onClick={() => removeFromDesk(t.sourceId)}
                      disabled={!!tileBusy}
                      title={`Take ${t.label} off the desk`}
                      style={{
                        fontFamily: MO, fontSize: 15, lineHeight: 1, width: 26, borderRadius: 5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: tileBusy ? 'not-allowed' : 'pointer', flexShrink: 0, padding: 0,
                        background: 'transparent', color: P.dim,
                        border: `0.5px solid ${P.rule}`,
                      }}>×</button>
                  )}
                  </div>
                );
              })}
              {palette.length === 0 && (
                <div style={{ fontFamily: MO, fontSize: 10.5, color: P.dim, lineHeight: 1.5 }}>
                  {/* Two different empty states wore one sentence. An empty
                      DESK is not "already tiles" — it is a desk with nothing on
                      it, and telling an operator who has just arrived that
                      their pieces are already paired is a dead end. */}
                  {doc.prototypes.length === 0
                    ? 'Nothing on the desk yet — add a carton above, or open a finished layout from Auto Imposer.'
                    : (addMode === 'pair'
                      ? "Nothing here can be paired — the layout's pieces are already tiles."
                      : 'No carton on the desk can be placed.')}
                </div>
              )}
            </div>

            <div style={{ fontFamily: FN, fontSize: 10, color: P.dim, marginTop: 8, lineHeight: 1.45 }}>
              {addMode !== 'pair'
                ? 'Dropped in the first clear spot that honours kerf and margins — then drag it where you want it.'
                : (palette.some((e) => e.prepress)
                  ? 'A carton prepress knows gets its real tile built by prepress, under the same rules '
                    + 'and configuration this job ran with.'
                    + (allowCustomPairArrangement
                      ? ' Where both halves are the same blank (RTE, STE), ⧉ arranges a pair '
                        + 'yourself instead — a fused BL tile has no ⧉, because its panels are '
                        + 'resequenced and only prepress can do that.'
                      : '')
                  : 'Two stamps of the same carton, slid together until they touch at the kerf — '
                    + 'they move, turn and delete as one piece, and plate as two.')}
            </div>
          </RailSection>
          <RailSection title="Export">
            {exportError && (
              <div style={{
                marginBottom: 10, padding: '9px 11px', borderRadius: 6,
                background: P.dangerSoft, border: `0.5px solid ${P.dangerLine}`,
                fontFamily: FN, fontSize: 11, color: P.ink, whiteSpace: 'pre-line',
              }}>{exportError}</div>
            )}
            <button
              onClick={() => onExport(projected)}
              disabled={exporting || blocked}
              title={blockReason
                || 'Gang the edited sheet losslessly and download the press-ready PDF.'}
              style={{
                width: '100%', fontFamily: FN, fontSize: 12.5, fontWeight: 500, padding: '9px 16px',
                borderRadius: 6, border: 'none', background: P.accent, color: '#fff',
                cursor: exporting || blocked ? 'default' : 'pointer', opacity: exporting || blocked ? 0.4 : 1,
              }}>
              {exporting ? 'Building PDF…' : 'Download press-ready PDF'}
            </button>
            {onRemoveBordersChange && (
              <div style={{ marginTop: 9 }}>
                <RemoveBordersSwitch value={removeBorders} onChange={onRemoveBordersChange}
                  disabled={exporting} />
              </div>
            )}
            {pressSwitches && (
              <div style={{ marginTop: 9 }}>
                <PressSheetSwitches {...pressSwitches} disabled={exporting} />
              </div>
            )}
            {borderReport && (
              <div style={{ marginTop: 9 }}>
                <BorderReportList report={borderReport.report} requested={borderReport.requested} />
              </div>
            )}
            {pressReport && (
              <div style={{ marginTop: 9 }}>
                <PressReportList report={pressReport.report} marks={pressReport.marks}
                  bleed={pressReport.bleed} />
              </div>
            )}
            <div style={{ fontFamily: FN, fontSize: 10, color: P.dim, marginTop: 8, lineHeight: 1.45 }}>
              Goes out through the same lossless impose endpoint as an untouched nest — your
              edits are just different coordinates on the same call.
            </div>
          </RailSection>
          <RailSection title="Sheet" foldable open={openCard.sheet}
            onToggle={() => toggleCard('sheet')} summary={sheetSummary}>
            <div style={{ display: 'flex', gap: 10 }}>
              <label style={railField}>W ({unit})
                <LenInput mm={doc.sheet.width} onMm={(v) => commit(setSheet(doc, { width: v }), 'sheet.width')} stepMm={1} style={inp} />
              </label>
              <label style={railField}>H ({unit})
                <LenInput mm={doc.sheet.height} onMm={(v) => commit(setSheet(doc, { height: v }), 'sheet.height')} stepMm={1} style={inp} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              {SHEET_PRESETS.map((p) => {
                const on = doc.sheet.width === p.w && doc.sheet.height === p.h;
                return (
                  <button key={p.l} onClick={() => commit(setSheet(doc, { width: p.w, height: p.h }))} style={{
                    fontFamily: MO, fontSize: 10, padding: '4px 9px', borderRadius: 4, cursor: 'pointer',
                    background: on ? P.ink : 'transparent', color: on ? P.sheet : P.muted,
                    border: `0.5px solid ${on ? P.ink : P.rule}`,
                  }}>{p.l}</button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <label style={railField}>Kerf ({unit})
                <LenInput mm={doc.sheet.kerf} onMm={(v) => commit(setSheet(doc, { kerf: v }), 'sheet.kerf')} stepMm={0.5} style={inp} />
              </label>
            </div>
            <div style={{ fontFamily: FN, fontSize: 10, color: P.dim, marginTop: 10, lineHeight: 1.45 }}>
              Resizing never moves your cartons. Anything that no longer fits is flagged
              on the sheet — what to drop is your call, not the tool's.
            </div>
          </RailSection>
          <RailSection title="Margins" foldable open={openCard.margins}
            onToggle={() => toggleCard('margins')} summary={marginSummary} tone={marginError ? P.warn : null}>
            <MarginFields
              mode={marginMode}
              onMode={(next) => {
                if (next === marginMode) return;
                // Switching the editor must not move a strip the operator set.
                // Collapsing to one number takes the LARGEST edge, so nothing
                // ends up with less clearance than it already had.
                if (next === 'uniform') {
                  const u = collapseToUniform(m);
                  commit(setSheet(doc, { margins: { top: u, right: u, bottom: u, left: u } }), 'margins.mode');
                }
                setMarginMode(next);
              }}
              uniform={uniformMargin != null ? uniformMargin : collapseToUniform(m)}
              sides={m}
              onUniform={(v) => commit(setSheet(doc, { margins: { top: v, right: v, bottom: v, left: v } }), 'margins.uniform')}
              onSides={(next) => commit(setSheet(doc, { margins: next }), 'margins.sides')}
              error={marginError}
            />
          </RailSection>
          <RailSection title="Shortcuts" foldable open={openCard.keys}
            onToggle={() => toggleCard('keys')} summary={'⌫ ⇧-click scroll ⌘Z'}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontFamily: MO, fontSize: 10, color: P.muted }}>
              {[
                ['Drag', 'move · Alt to ignore snap'],
                ['Drag empty', 'rubber-band select · ⇧ to add'],
                ['Double-click', 'select every piece of that carton'],
                ['Space-drag', 'pan'],
                ['Arrows', `nudge ${fmtLen(NUDGE.normal)} · ⇧ ${fmtLen(NUDGE.coarse)} · ⌥ ${fmtLen(NUDGE.fine)}`],
                ['R / ⇧R', 'rotate 90° CCW / CW'],
                ['⌘D', 'duplicate'],
                ['⌫', 'remove'],
                ['⇧-click', 'add to selection'],
                ['Scroll', 'zoom'],
                ['⌘Z / ⇧⌘Z', 'undo / redo'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ color: P.dim }}>{k}</span><span style={{ textAlign: 'right' }}>{v}</span>
                </div>
              ))}
            </div>
          </RailSection>
        </aside>

        {/* ── MAIN ── */}
        <main style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Stat label="Cartons" value={stats.cartons} />
            <Stat label="Pieces" value={stats.pieces} />
            <Stat label="Utilization" value={`${(stats.utilization * 100).toFixed(1)}% gross`} />
            <Stat label="Net" value={`${(stats.netUtilization * 100).toFixed(1)}% of usable`} />
            <Stat label="Sheet" value={`${fmtLen(doc.sheet.width)} × ${fmtLen(doc.sheet.height)}`} />
            <Stat label="Issues" value={issueCount || '—'}
              tone={conflicts.overlaps.length ? P.danger : (issueCount ? P.warn : P.ok)} />
          </div>

          {/* The checks ride ON the sheet. They used to be the fourth card in
              the rail, which is exactly below the fold while an operator is
              dragging pieces — the one moment the answer matters and the one
              moment they were looking somewhere else. */}
          <div style={{ position: 'relative' }}>
            <EditorCanvas
              doc={doc}
              conflicts={conflicts}
              selection={selection}
              onSelect={setSelection}
              onSelectSimilar={selectSameCarton}
              onCommit={(uids, dx, dy) => commit(movePieces(doc, uids, dx, dy))}
            />
            <CanvasChecks issues={issues} onSelect={setSelection} />
            {/* The actions belong where the pieces are. They were in the
                inspector BELOW the sheet, which meant selecting a carton on the
                canvas and then looking away from it to turn or delete it. */}
            <CanvasSelectionBar
              count={selection.length}
              label={selection.length === 1
                ? `${oneProto?.label || ''}${describePiece(oneProto)}`
                : `${selection.length} pieces`}
              size={oneGeo ? `${fmtLen(oneGeo.bbox.w)} × ${fmtLen(oneGeo.bbox.h)}` : null}
              similar={similarGroups}
              onSelectGroup={(g) => setSelection(g.kind === 'source'
                ? piecesOfSource(doc, g.id)
                : piecesOfCartonType(doc, g.id))}
              onSelectAll={() => setSelection(doc.pieces.map((p) => p.uid))}
              onRotate={(by) => rotateSel(by, null)}
              onDuplicate={duplicateSel}
              onRemove={deleteSel}
              onClear={() => setSelection([])}
            />
            <CanvasTransformPad
              count={selection.length}
              x={one ? one.x : null}
              y={one ? one.y : null}
              rot={one ? one.rot : null}
              onNudge={(dx, dy) => moveSel(dx, dy)}
              onRotateTo={(deg) => rotateSelTo(deg)}
              onRotateBy={(deg) => rotateSel(deg)}
              onSetX={(v) => one && commit(setPiecePosition(doc, one.uid, v, one.y), 'piece.x')}
              onSetY={(v) => one && commit(setPiecePosition(doc, one.uid, one.x, v), 'piece.y')}
              onSetRot={(v) => one && commit(
                rotatePieces(doc, [one.uid], { to: normalizeAngle(v) }), 'piece.rot')}
            />
          </div>

          {/* The inspector is gone: everything it held — the selection's name, the
              quick actions, and X / Y / rotation — now sits on the sheet, where
              the pieces are. What remains is a line of help for an empty
              selection, which has no business taking a card of its own. */}
        </main>
      </div>
    </div>
  );
}

/**
 * How a selected piece describes itself.
 *
 * A pair the operator built and a tile the prepress engine fused are both "two
 * cartons in one piece" and are NOT the same thing — one is two stamps of an
 * unchanged die, the other a fused die that may carry generated flaps. Naming
 * them apart is the difference between an operator knowing what will be cut and
 * guessing.
 */
function describePiece(proto) {
  if (!proto || proto.cartons <= 1) return '';
  if (proto.origin === 'tile') {
    const turn = proto.tile?.turn ? `, second turned ${proto.tile.turn}°` : ', both the same way';
    return ` · hand-built pair (${proto.tile?.axis === 'y' ? 'stacked' : 'across'}${turn})`;
  }
  if (proto.origin === 'prepress-tile') {
    // The variants are the whole reason this had to come from prepress: they
    // name the panel order each half was resequenced into.
    const v = (proto.variants || []).filter(Boolean);
    return ` · ${proto.cartonType || 'prepress'} tile built by prepress`
      + (v.length ? ` (${v.join(' + ')})` : '');
  }
  return ` · prepress tile of ${proto.cartons}`;
}

/** Rotation snap, degrees. 15 contains every quarter and every 45. */
const ROT_SNAP = 15;

/**
 * Position and rotation for the selection, as one instrument on the sheet.
 *
 * A RING you drag to rotate, with a PLUS of nudge buttons inside it. These were
 * three numeric fields in the inspector below the canvas, which is the wrong
 * shape for the job twice over: on a tall sheet the inspector is off screen, and
 * turning a carton by typing 90 into a box is not how anyone thinks about
 * turning a carton.
 *
 * ─── THE RING ──────────────────────────────────────────────────────────────
 * 0° at 3 o'clock, increasing counter-clockwise — the model's own convention
 * (CCW, Y-up), so the handle's screen direction IS the rotation and nothing is
 * translated between what is drawn and what is stored. Ticks mark the quarter
 * turns, which is how cartons actually gang.
 *
 * Snaps to 15° (so every quarter and every 45 is reachable without care); hold
 * Alt for free rotation. A single selection rotates to an ABSOLUTE angle, so the
 * handle means what it shows. A multi-selection rotates BY the drag's delta,
 * because several pieces at different angles have no single angle to show.
 *
 * ─── THE PAD ───────────────────────────────────────────────────────────────
 * One step per click, the same step the arrow keys use, and the same modifiers:
 * Shift for 10 mm, Alt for 0.1 mm. Press and hold to repeat — without that a
 * 1 mm button is a way to look busy rather than a way to move a carton.
 */
export function CanvasTransformPad({
  count, x, y, rot, onNudge, onRotateTo, onRotateBy, onSetX, onSetY, onSetRot,
}) {
  const { P, MO, inp, unit } = useTheme();
  const ringRef = useRef(null);
  const holdRef = useRef(null);
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  // How far THIS drag has turned, for a selection that has no single angle of
  // its own. Without it the handle would sit still while several cartons turned
  // underneath it, which reads as a broken control.
  const [live, setLive] = useState(null);

  useEffect(() => () => clearTimeout(holdRef.current), []);
  if (!count) return null;

  const single = count === 1 && Number.isFinite(rot);
  const shown = live != null ? live : (single ? rot : 0);

  const R = 52, CX = 62, CY = 62;              // ring radius / widget centre
  const rad = (deg) => (deg * Math.PI) / 180;
  // Screen point for a model angle: CCW, 0 at 3 o'clock, Y inverted for SVG.
  const at = (deg, r) => [CX + r * Math.cos(rad(deg)), CY - r * Math.sin(rad(deg))];

  const angleFrom = (e) => {
    const b = ringRef.current.getBoundingClientRect();
    return (Math.atan2(-(e.clientY - (b.top + b.height / 2)),
      e.clientX - (b.left + b.width / 2)) * 180) / Math.PI;
  };

  const beginRing = (e) => {
    e.preventDefault();
    dragRef.current = { start: angleFrom(e), base: shown, applied: 0 };
    setDragging(true);
    setLive(shown);
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      let delta = angleFrom(ev) - d.start;
      if (!ev.altKey) {
        const target = Math.round((d.base + delta) / ROT_SNAP) * ROT_SNAP;
        delta = target - d.base;
      }
      setLive(d.base + delta);
      if (single) {
        onRotateTo(d.base + delta);
      } else {
        onRotateBy(delta - d.applied);
        d.applied = delta;
      }
    };
    const up = () => {
      dragRef.current = null;
      setDragging(false);
      setLive(null);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const stepFor = (m) => (m.shift ? NUDGE.coarse : (m.alt ? NUDGE.fine : NUDGE.normal));
  const beginHold = (dx, dy) => (e) => {
    e.preventDefault();
    const m = { shift: e.shiftKey, alt: e.altKey };
    const fire = () => onNudge(dx * stepFor(m), dy * stepFor(m));
    fire();
    const repeat = () => { fire(); holdRef.current = setTimeout(repeat, 55); };
    holdRef.current = setTimeout(repeat, 320);
  };
  const endHold = () => { clearTimeout(holdRef.current); holdRef.current = null; };

  const arrow = (dx, dy, glyph, label, pos) => (
    <button
      onMouseDown={beginHold(dx, dy)} onMouseUp={endHold} onMouseLeave={endHold}
      title={`${label} — ${NUDGE.normal} mm, ⇧ ${NUDGE.coarse} mm, ⌥ ${NUDGE.fine} mm. Hold to repeat.`}
      style={{
        position: 'absolute', ...pos, width: 22, height: 22, padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: MO, fontSize: 11, lineHeight: 1, cursor: 'pointer',
        background: P.elev, color: P.ink, border: `0.5px solid ${P.rule}`,
        borderRadius: 5,
      }}>{glyph}</button>
  );

  const num = (label, value, onCommit, isLen) => (
    <label style={{ display: 'block', fontFamily: MO, fontSize: 8.5, color: P.dim, minWidth: 0 }}>
      <span style={{ display: 'block', letterSpacing: 0.5, marginBottom: 1 }}>{label}</span>
      {value == null ? (
        <div style={{ ...inp, marginTop: 0, padding: '3px 5px', fontSize: 11,
          color: P.dim, textAlign: 'center' }}>—</div>
      ) : isLen ? (
        <LenInput mm={value} onMm={onCommit} stepMm={1}
          style={{ ...inp, marginTop: 0, padding: '3px 5px', fontSize: 11 }} />
      ) : (
        <input type="number" step={1} value={Math.round(value * 100) / 100}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            if (Number.isFinite(v)) onCommit(v);
          }}
          style={{ ...inp, marginTop: 0, padding: '3px 5px', fontSize: 11 }} />
      )}
    </label>
  );

  const [hx, hy] = at(shown, R);

  return (
    <div style={{
      position: 'absolute', left: 10, bottom: 10, zIndex: 3, pointerEvents: 'auto',
      padding: 8, borderRadius: 12,
      background: P.surface, border: `0.5px solid ${P.rule}`,
      backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      boxShadow: `0 4px 14px ${P.isDark ? 'rgba(0,0,0,.45)' : 'rgba(0,0,0,.10)'}`,
    }}>
      <div ref={ringRef} style={{ position: 'relative', width: 124, height: 124 }}>
        <svg width={124} height={124} style={{ display: 'block', position: 'absolute', inset: 0 }}>
          <circle cx={CX} cy={CY} r={R} fill="none" stroke={P.rule} strokeWidth={12} />
          <circle cx={CX} cy={CY} r={R} fill="none" stroke={P.ruleSoft} strokeWidth={12}
            style={{ cursor: dragging ? 'grabbing' : 'grab' }}
            onMouseDown={beginRing} />
          {/* Quarter turns, marked because that is how cartons gang. */}
          {[0, 90, 180, 270].map((d) => {
            const [x1, y1] = at(d, R - 6);
            const [x2, y2] = at(d, R + 6);
            return <line key={d} x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={P.dim} strokeWidth={1} pointerEvents="none" />;
          })}
          <line x1={CX} y1={CY} x2={hx} y2={hy} stroke={P.accent} strokeWidth={1.5}
            pointerEvents="none" opacity={single || live != null ? 1 : 0.35} />
          <circle cx={hx} cy={hy} r={6} fill={P.accent} stroke={P.sheet} strokeWidth={1.5}
            style={{ cursor: dragging ? 'grabbing' : 'grab' }}
            onMouseDown={beginRing} />
        </svg>

        {/* The plus, inside the ring. */}
        {arrow(0, 1, '▲', 'Up', { left: 51, top: 24 })}
        {arrow(-1, 0, '◀', 'Left', { left: 28, top: 51 })}
        {arrow(1, 0, '▶', 'Right', { left: 74, top: 51 })}
        {arrow(0, -1, '▼', 'Down', { left: 51, top: 78 })}
        <div title={single ? 'Rotation' : 'Several pieces — the ring turns them all by the same amount'}
          style={{
            position: 'absolute', left: 51, top: 51, width: 22, height: 22,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: MO, fontSize: 9, color: P.dim, pointerEvents: 'none',
          }}>
          {single
            ? `${Math.round(shown)}°`
            : (live != null ? `${live > 0 ? '+' : ''}${Math.round(live)}°` : `×${count}`)}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginTop: 6, width: 124 }}>
        {num(`X ${unit}`, single ? x : null, onSetX, true)}
        {num(`Y ${unit}`, single ? y : null, onSetY, true)}
        {num('ROT °', single ? rot : null, onSetRot, false)}
      </div>
    </div>
  );
}

/**
 * What you can do to the selection, pinned to the sheet.
 *
 * These lived in the inspector under the canvas, which meant picking a carton
 * on the sheet and then looking away from it — past the whole canvas — to turn
 * or delete it. On a tall sheet the inspector is off screen entirely.
 *
 * Fixed to the canvas corner rather than floating beside the selection: a bar
 * that follows the piece moves under the cursor exactly while you are aiming at
 * that piece, and would have to dodge the sheet edges as well.
 *
 * Only the quick actions come here. The X / Y / rotation fields stay in the
 * inspector — typing an exact coordinate is deliberate work, not something done
 * mid-drag, and four numeric inputs floating over the artwork would cover it.
 *
 * Appears only with a selection, so an empty sheet is unobstructed.
 */
export function CanvasSelectionBar({
  count, label, size,
  // Defaulted, not required: a bar that throws because one optional offer was
  // not passed is a worse failure than a bar with one fewer button.
  similar = [], onSelectGroup = null, onSelectAll = null,
  onRotate, onDuplicate, onRemove, onClear,
}) {
  const { P, MO } = useTheme();
  const [menu, setMenu] = useState(false);
  if (!count) return null;

  const act = (tone) => ({
    pointerEvents: 'auto',
    fontFamily: MO, fontSize: 11, padding: '4px 9px', borderRadius: 5,
    cursor: 'pointer', whiteSpace: 'nowrap',
    background: 'transparent',
    color: tone === 'danger' ? P.danger : P.ink,
    border: `0.5px solid ${tone === 'danger' ? P.dangerLine : P.rule}`,
  });

  return (
    <div style={{
      position: 'absolute', top: 10, left: 10, zIndex: 3,
      pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: 6,
      padding: '5px 6px 5px 10px', borderRadius: 999,
      maxWidth: 'calc(100% - 20px)', flexWrap: 'wrap',
      background: P.surface, border: `0.5px solid ${P.rule}`,
      backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      boxShadow: `0 1px 3px ${P.isDark ? 'rgba(0,0,0,.4)' : 'rgba(0,0,0,.08)'}`,
    }}>
      <span style={{
        fontFamily: MO, fontSize: 10.5, color: P.muted, maxWidth: 220,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{label}</span>
      {/* The placed footprint at the CURRENT rotation — it changes as the ring
          turns, which is half of why it is worth showing next to the ring. */}
      {size && (
        <span style={{ fontFamily: MO, fontSize: 10, color: P.dim, whiteSpace: 'nowrap' }}>
          {size}
        </span>
      )}
      <span style={{ width: 1, height: 14, background: P.rule }} />
      {/* "Select all of this kind" appears with a selection and nowhere else —
          it is a question you can only ask about something you have picked, so
          it costs no permanent space on the sheet. */}
      {similar.length > 0 && onSelectGroup && (
        <span style={{ position: 'relative', pointerEvents: 'auto' }}>
          <button style={act()} onClick={() => setMenu((v) => !v)}
            title="Select every piece of this carton, or of this carton type">
            Select {menu ? '▴' : '▾'}
          </button>
          {menu && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 4,
              minWidth: 210, padding: 4, borderRadius: 8,
              background: P.surface, border: `0.5px solid ${P.rule}`,
              boxShadow: `0 6px 20px ${P.isDark ? 'rgba(0,0,0,.5)' : 'rgba(0,0,0,.12)'}`,
            }}>
              {similar.map((g) => (
                <button key={g.key}
                  onClick={() => { onSelectGroup(g); setMenu(false); }}
                  style={{
                    display: 'flex', width: '100%', gap: 8, alignItems: 'baseline',
                    fontFamily: MO, fontSize: 11, padding: '6px 8px', borderRadius: 5,
                    cursor: 'pointer', textAlign: 'left',
                    background: 'transparent', color: P.ink, border: 'none',
                  }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {g.label}
                  </span>
                  <span style={{ color: P.dim, fontSize: 10 }}>{g.pieces}</span>
                </button>
              ))}
              <button onClick={() => { onSelectAll(); setMenu(false); }}
                style={{
                  display: 'flex', width: '100%', gap: 8, alignItems: 'baseline',
                  fontFamily: MO, fontSize: 11, padding: '6px 8px', borderRadius: 5,
                  cursor: 'pointer', textAlign: 'left', borderTop: `0.5px solid ${P.ruleSoft}`,
                  background: 'transparent', color: P.muted, border: 'none',
                }}>
                <span style={{ flex: 1 }}>Everything on the sheet</span>
                <span style={{ color: P.dim, fontSize: 10 }}>⌘A</span>
              </button>
            </div>
          )}
        </span>
      )}
      <button style={act()} onClick={() => onRotate(90)} title="Rotate 90° counter-clockwise (R)">↺ 90°</button>
      <button style={act()} onClick={() => onRotate(-90)} title="Rotate 90° clockwise (⇧R)">↻ 90°</button>
      <button style={act()} onClick={onDuplicate} title="Duplicate (⌘D)">Duplicate</button>
      <button style={act('danger')} onClick={onRemove} title="Remove from the sheet (⌫)">Remove</button>
      <button style={{ ...act(), padding: '4px 7px', color: P.dim }}
        onClick={onClear} title="Deselect (Esc)">✕</button>
    </div>
  );
}

/**
 * The sheet's problems, pinned to the sheet.
 *
 * Collapsed it is one chip naming the worst thing wrong — an operator dragging
 * a die needs "2 overlaps" in their peripheral vision, not a panel to read.
 * Clicking opens the detail and the Select actions.
 *
 * It does NOT open itself when a problem appears. A panel that expands under
 * the cursor mid-drag moves the thing you were aiming at; the chip turning red
 * says the same thing without touching the sheet.
 *
 * The container is pointer-transparent so panning still works across the whole
 * canvas; only the chip and the open panel take clicks.
 */
export function CanvasChecks({ issues, onSelect }) {
  const { P, MO, FN } = useTheme();
  const [open, setOpen] = useState(false);
  const worst = issues.find((i) => i.tone === 'danger') || issues[0] || null;
  const blocking = issues.some((i) => i.tone === 'danger');

  const tone = !issues.length ? 'ok' : (blocking ? 'danger' : 'warn');
  const colors = {
    ok: { fg: P.ok, bg: P.okSoft, line: P.okLine },
    warn: { fg: P.warn, bg: P.warnSoft, line: P.warnLine },
    danger: { fg: P.danger, bg: P.dangerSoft, line: P.dangerLine },
  }[tone];

  return (
    <div style={{
      position: 'absolute', top: 10, right: 10, zIndex: 3,
      pointerEvents: 'none', display: 'flex', flexDirection: 'column',
      alignItems: 'flex-end', gap: 6, maxWidth: 'min(340px, 60%)',
    }}>
      <button
        onClick={() => issues.length && setOpen((v) => !v)}
        title={issues.length ? 'Show what is wrong with this sheet' : 'Nothing wrong with this sheet'}
        style={{
          pointerEvents: 'auto',
          display: 'flex', alignItems: 'center', gap: 7,
          fontFamily: MO, fontSize: 11, padding: '5px 10px', borderRadius: 999,
          cursor: issues.length ? 'pointer' : 'default',
          background: colors.bg, color: colors.fg,
          border: `0.5px solid ${colors.line}`,
          // Readable over both the sheet and the dot grid behind it.
          backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
          boxShadow: `0 1px 3px ${P.isDark ? 'rgba(0,0,0,.4)' : 'rgba(0,0,0,.08)'}`,
        }}>
        <span>{issues.length ? '⚠' : '✓'}</span>
        <span>{issues.length ? worst.label : 'Sheet is clean'}</span>
        {issues.length > 1 && (
          <span style={{ opacity: 0.75 }}>+{issues.length - 1}</span>
        )}
        {issues.length > 0 && <span style={{ opacity: 0.6 }}>{open ? '▴' : '▾'}</span>}
      </button>

      {open && issues.length > 0 && (
        <div style={{
          pointerEvents: 'auto', width: 300, maxHeight: 300, overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 6,
          padding: 8, borderRadius: 8,
          background: P.surface, border: `0.5px solid ${P.rule}`,
          boxShadow: `0 6px 20px ${P.isDark ? 'rgba(0,0,0,.5)' : 'rgba(0,0,0,.12)'}`,
        }}>
          {issues.map((i) => (
            <IssueRow key={i.key} tone={i.tone} label={i.label} detail={i.detail}
              onShow={i.uids && i.uids.length ? () => onSelect(i.uids) : null} />
          ))}
          <div style={{ fontFamily: FN, fontSize: 9.5, color: P.dim, lineHeight: 1.4 }}>
            Only overlaps block the export — the rest are yours to judge.
          </div>
        </div>
      )}
    </div>
  );
}

/** A labelled row of mutually exclusive options. */
/**
 * One card in the left rail, optionally foldable.
 *
 * The rail used to be five always-open cards in setup-first order, so the two
 * things an operator touches CONSTANTLY — the carton palette and Export — sat
 * under two they set ONCE per session, and were pushed below the fold. The
 * palette is also the tallest card, so it was the one that got cut.
 *
 * Folding is offered only to the set-once cards. A fold that hides a value the
 * operator still needs is a worse citizen than a scroll, so a folded card keeps
 * its numbers on the heading — `summary` is what it reads when closed.
 */
function RailSection({ title, summary, foldable = false, open = true, onToggle, tone = null, children }) {
  const { P, MO } = useTheme();
  const card = {
    background: P.surface, border: `0.5px solid ${tone || P.rule}`,
    borderRadius: 10, padding: 14,
  };
  const head = {
    fontFamily: MO, fontSize: 10, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: tone || P.dim,
  };
  if (!foldable) {
    return (
      <section style={card}>
        <span style={head}>{title}</span>
        {children}
      </section>
    );
  }
  return (
    <section style={card}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        title={open ? `Collapse ${title}` : `Expand ${title}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          textAlign: 'left',
        }}>
        <span style={head}>{title}</span>
        {!open && summary && (
          <span style={{
            fontFamily: MO, fontSize: 10, color: tone || P.muted, flex: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{summary}</span>
        )}
        <span style={{ marginLeft: 'auto', fontFamily: MO, fontSize: 10, color: P.dim }}>
          {open ? '\u25B4' : '\u25BE'}
        </span>
      </button>
      {open && children}
    </section>
  );
}

function Choice({ label, value, onChange, options }) {
  const { P, MO } = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontFamily: MO, fontSize: 10, color: P.dim, width: 52 }}>{label}</span>
      <div style={{ display: 'flex', gap: 4, flex: 1 }}>
        {options.map(([v, lbl]) => (
          <button key={String(v)} onClick={() => onChange(v)} style={{
            flex: 1, fontFamily: MO, fontSize: 10, padding: '4px 6px', borderRadius: 4, cursor: 'pointer',
            background: value === v ? P.accentSoft : 'transparent',
            color: value === v ? P.accent : P.muted,
            border: `0.5px solid ${value === v ? P.accentLine : P.rule}`,
          }}>{lbl}</button>
        ))}
      </div>
    </div>
  );
}

/** One number in the strip above the sheet. */
function Stat({ label, value, tone }) {
  const { P, MO } = useTheme();
  return (
    <div style={{ padding: '8px 12px', borderRadius: 6, background: P.elev, border: `0.5px solid ${P.ruleSoft}` }}>
      <div style={{ fontFamily: MO, fontSize: 9, color: P.dim, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontFamily: MO, fontSize: 14, fontWeight: 500, color: tone || P.ink, marginTop: 3 }}>{value}</div>
    </div>
  );
}

function IssueRow({ tone, label, detail, onShow }) {
  const { P, MO, FN } = useTheme();
  const c = tone === 'danger' ? P.danger : P.warn;
  return (
    <div style={{
      padding: '8px 10px', borderRadius: 6,
      background: tone === 'danger' ? P.dangerSoft : P.warnSoft,
      border: `0.5px solid ${tone === 'danger' ? P.dangerLine : P.warnLine}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: MO, fontSize: 11, color: c, flex: 1 }}>{label}</span>
        {onShow && (
          <button onClick={onShow} style={{
            fontFamily: MO, fontSize: 9.5, padding: '2px 7px', borderRadius: 3, cursor: 'pointer',
            background: 'transparent', color: c, border: `0.5px solid ${c}`,
          }}>Select</button>
        )}
      </div>
      <div style={{ fontFamily: FN, fontSize: 10, color: P.muted, marginTop: 3, lineHeight: 1.4 }}>{detail}</div>
    </div>
  );
}
